import { config } from '@caixa/config';
import type { VendaAjustesSnapshot } from '@caixa/types';
import { formatVendaApiError } from '@caixa/lib/formatVendaApiError';
import { normalizarRelatorioDia, type RelatorioDiaCompleto } from '@caixa/relatorio/types';

export interface PromocaoGrupoProduto {
  produto_id: number;
  produto_nome: string;
}

export interface PromocaoGrupoCaixa {
  nome_grupo: string;
  quantidade_obrigatoria: number;
  produtos: PromocaoGrupoProduto[];
}

export interface ProdutoCategoriaPromo {
  id: number;
  nome: string;
  preco_venda: number | null;
  possui_composicao?: boolean;
  permite_decimal_caixa?: boolean;
}

/** Uma linha da promo por categoria: N escolhas entre produtos dessa categoria */
export interface PromocaoCategoriaGrupoCaixa {
  categoria: string;
  quantidade: number;
  produtos: ProdutoCategoriaPromo[];
}

export interface ProdutoCaixa {
  id: number;
  nome: string;
  preco_venda: number | null;
  /** true quando produto tem composição cadastrada */
  possui_composicao?: boolean;
  permite_decimal_caixa?: boolean;
  /** Unidade de venda: unidade | quilo | grama | litro | mililitro */
  unidade_venda?: string | null;
  controla_estoque?: boolean;
  /** ID da promoção se este produto for o "virtual" de uma promo */
  promocao_id?: number | null;
  /** 'configuravel' = modal por grupos; 'categoria' = modal por categorias e quantidades */
  promocao_tipo?: 'fixa' | 'configuravel' | 'categoria' | null;
  /** Grupos e produtos permitidos (só quando promocao_tipo === 'configuravel') */
  promocao_grupos?: PromocaoGrupoCaixa[] | null;
  /** Promoção por categoria: blocos com quantidade de escolhas por categoria */
  promocao_categorias_grupos?: PromocaoCategoriaGrupoCaixa[] | null;
}

/** Resposta do endpoint list.php: { ok: true, produtos: [...] } */
interface ListResponse {
  ok?: boolean;
  produtos?: unknown[];
}

const defaultOptions: RequestInit = {
  credentials: config.credentials,
  headers: { 'Content-Type': 'application/json' },
};

const isDev = import.meta.env.DEV;

/**
 * Busca produtos do sistema de gestão (com e sem composição).
 * Endpoint: GET /api/produtos/list-caixa.php
 * Retorno: { ok: true, produtos: [{ id, nome, preco_venda, possui_composicao }] }
 */
export async function fetchProdutosCaixa(): Promise<ProdutoCaixa[]> {
  const url = `${config.BASE_URL}/produtos/list-caixa.php`;
  try {
    const res = await fetch(url, { ...defaultOptions, method: 'GET' });
    const text = await res.text();
    const trimmed = text.trim();

    if (isDev) {
      console.log('[CAIXA] fetch produtos:', { url, status: res.status, ok: res.ok, bodyLength: trimmed.length });
    }

    if (res.status === 401 || res.status === 403) {
      const target = window.top ?? window;
      const path = target.location.pathname.replace(/\/+$/, "") || "/";
      // Sessão expirada no caixa — recarrega a rota SPA /caixa (login via RequireAuth)
      if (path === "/caixa" || path.startsWith("/caixa/")) {
        target.location.replace("/caixa");
      }
      return [];
    }

    if (!trimmed) return [];

    const data: ListResponse = JSON.parse(trimmed);
    const raw = data?.produtos;

    if (!Array.isArray(raw)) {
      if (isDev) console.log('[CAIXA] Resposta sem produtos ou formato inválido:', data);
      return [];
    }

    const produtos: ProdutoCaixa[] = raw.map((p: unknown) => {
      const row = p as Record<string, unknown>;
      const promocaoId = row.promocao_id != null && row.promocao_id !== '' ? Number(row.promocao_id) : null;
      const pt = String(row.promocao_tipo ?? '')
        .trim()
        .toLowerCase();
      let promocaoTipo: 'configuravel' | 'categoria' | 'fixa' | null =
        pt === 'configuravel'
          ? 'configuravel'
          : pt === 'categoria'
            ? 'categoria'
            : pt === 'fixa'
              ? 'fixa'
              : null;
      const rawGrupos = row.promocao_grupos;
      const promocao_grupos: PromocaoGrupoCaixa[] | null = Array.isArray(rawGrupos)
        ? rawGrupos.map((g: unknown) => {
            const gr = g as Record<string, unknown>;
            const prods = Array.isArray(gr.produtos) ? gr.produtos.map((pr: unknown) => {
              const ppr = pr as Record<string, unknown>;
              return { produto_id: Number(ppr.produto_id ?? 0), produto_nome: String(ppr.produto_nome ?? '') };
            }) : [];
            return {
              nome_grupo: String(gr.nome_grupo ?? ''),
              quantidade_obrigatoria: Number(gr.quantidade_obrigatoria ?? 1),
              produtos: prods,
            };
          })
        : null;
      const rawCatGrupos = row.promocao_categorias_grupos;
      const promocao_categorias_grupos: PromocaoCategoriaGrupoCaixa[] | null = Array.isArray(rawCatGrupos)
        ? rawCatGrupos.map((g: unknown) => {
            const gr = g as Record<string, unknown>;
            const prods = Array.isArray(gr.produtos)
              ? gr.produtos.map((x: unknown) => {
                  const z = x as Record<string, unknown>;
                  return {
                    id: Number(z.id) || 0,
                    nome: String(z.nome ?? ''),
                    preco_venda: z.preco_venda != null ? Number(z.preco_venda) : null,
                    possui_composicao: z.possui_composicao === true || z.possui_composicao === 1,
                    permite_decimal_caixa: z.permite_decimal_caixa === true || z.permite_decimal_caixa === 1,
                  };
                })
              : [];
            return {
              categoria: String(gr.categoria ?? ''),
              quantidade: Math.max(1, Number(gr.quantidade) || 1),
              produtos: prods,
            };
          })
        : null;

      /** Se o backend enviou grupos de categoria mas o tipo veio vazio/errado, ainda assim é promo por categoria. */
      if (
        !promocaoTipo &&
        Array.isArray(promocao_categorias_grupos) &&
        promocao_categorias_grupos.length > 0
      ) {
        promocaoTipo = 'categoria';
      }

      return {
        id: Number(row.id) || 0,
        nome: String(row.nome ?? row.label ?? ''),
        preco_venda: row.preco_venda != null ? Number(row.preco_venda) : null,
        possui_composicao: row.possui_composicao === true || row.possui_composicao === 1,
        permite_decimal_caixa: row.permite_decimal_caixa === true || row.permite_decimal_caixa === 1,
        unidade_venda: row.unidade_venda != null ? String(row.unidade_venda) : 'unidade',
        controla_estoque:
          row.controla_estoque === undefined || row.controla_estoque === null
            ? true
            : row.controla_estoque === true || row.controla_estoque === 1,
        promocao_id: promocaoId ?? undefined,
        promocao_tipo: promocaoTipo ?? undefined,
        promocao_grupos: promocao_grupos ?? undefined,
        promocao_categorias_grupos:
          promocaoTipo === 'categoria' && promocao_categorias_grupos && promocao_categorias_grupos.length > 0
            ? promocao_categorias_grupos
            : promocaoTipo === 'categoria'
              ? []
              : undefined,
      };
    });

    if (isDev) console.log('[CAIXA] Produtos carregados:', produtos.length);
    return produtos;
  } catch (err) {
    if (isDev) console.warn('[CAIXA] Erro ao buscar produtos:', err);
    return [];
  }
}

export interface VendaCriarPayload {
  comanda_id?: number;
  atendimento_origem?: { identificacao: string; tipo: 'mesa' | 'comanda' | 'ambos' };
  itens: Array<{
    produto_id: number;
    quantidade: number;
    preco_unitario: number;
    nome_produto: string;
    /** Mesma propriedade do Cardápio / comandas_abertas_itens.observacao */
    observacao?: string;
    /** Para promo configurável: itens escolhidos nos grupos (para baixa de estoque e histórico) */
    promocao_id?: number;
    itens_escolhidos?: Array<{ produto_id: number; nome_produto: string; quantidade: number; preco_unitario: number }>;
  }>;
  total: number;
  ajustes?: VendaAjustesSnapshot;
  pagamento: {
    pix: number;
    dinheiro: number;
    credito: number;
    debito: number;
    dinheiroRecebido?: number;
    troco?: number;
  };
  validar_estoque_apenas?: boolean;
}

/**
 * Registra venda no backend (grava no banco e baixa estoque).
 * Em caso de estoque insuficiente retorna 400 com mensagem no body.
 */
export type NfceEmitResponse = {
  success: boolean;
  authorized?: boolean;
  pending: boolean;
  status?: string;
  ref: string;
  danfe_url?: string;
  pdf_url?: string;
  chave?: string;
  numero?: string;
  serie?: string;
  protocolo?: string;
  data_emissao?: string;
  qrcode_url?: string;
  url_consulta?: string;
  emitente?: { nome?: string; cnpj?: string; ie?: string; endereco?: string };
  destinatario?: { cpf?: string; cnpj?: string; nome?: string };
  itens?: Array<{ descricao?: string; quantidade?: number; valor_unitario?: number; total?: number }>;
  pagamentos?: Array<{ descricao?: string; valor?: number }>;
  subtotal?: number;
  desconto?: number;
  total?: number;
  valor_total_tributos?: number;
};

type VendaApiResponse = {
  ok?: boolean;
  success?: boolean;
  error?: string;
  message?: string;
  details?: string;
  venda_id?: number;
};

async function postVendaCreate(payload: VendaCriarPayload): Promise<VendaApiResponse> {
  const url = `${config.BASE_URL}/vendas/create.php`;
  const res = await fetch(url, {
    ...defaultOptions,
    method: 'POST',
    body: JSON.stringify(payload),
  });
  const text = await res.text().then((t) => t.trim());
  let data: VendaApiResponse;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    throw new Error('Resposta inválida do servidor');
  }

  const failed =
    !res.ok ||
    data.ok === false ||
    data.success === false;

  if (failed) {
    const raw =
      (typeof data.error === 'string' && data.error.trim()) ||
      (typeof data.message === 'string' && data.message.trim()) ||
      (typeof data.details === 'string' && data.details.trim()) ||
      'Erro ao finalizar venda';
    throw new Error(formatVendaApiError(raw));
  }

  return data;
}

export async function vendasValidarEstoque(payload: VendaCriarPayload): Promise<void> {
  await postVendaCreate({
    ...payload,
    validar_estoque_apenas: true,
  });
}

/** Emite NFC-e no servidor (Focus NFe). */
export async function nfceEmitir(payload: {
  venda_id: number;
  /** CPF (11) ou CNPJ (14) — somente dígitos ou string vazia */
  documento?: string;
  /** @deprecated use documento */
  cpf?: string;
}): Promise<NfceEmitResponse> {
  const url = `${config.BASE_URL}/nfce/create.php`;
  const raw = (payload.documento ?? payload.cpf ?? "").replace(/\D/g, "");
  const res = await fetch(url, {
    ...defaultOptions,
    method: "POST",
    body: JSON.stringify({
      venda_id: payload.venda_id,
      documento: raw,
    }),
  });
  const text = await res.text().then((t) => t.trim());
  let data: {
    ok?: boolean;
    success?: boolean;
    authorized?: boolean;
    pending?: boolean;
    status?: string;
    ref?: string;
    error?: string;
    danfe_url?: string;
    pdf_url?: string;
    chave?: string;
    numero?: string;
    serie?: string;
    protocolo?: string;
    data_emissao?: string;
    qrcode_url?: string;
    url_consulta?: string;
    emitente?: { nome?: string; cnpj?: string; ie?: string; endereco?: string };
    destinatario?: { cpf?: string; cnpj?: string; nome?: string };
    itens?: Array<{ descricao?: string; quantidade?: number; valor_unitario?: number; total?: number }>;
    pagamentos?: Array<{ descricao?: string; valor?: number }>;
    subtotal?: number;
    desconto?: number;
    total?: number;
    valor_total_tributos?: number;
  };
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    throw new Error("Resposta inválida do servidor");
  }
  if (!res.ok || data.ok === false || data.success === false) {
    throw new Error(typeof data.error === "string" ? data.error : "Erro ao emitir NFC-e");
  }
  const ref = typeof data.ref === "string" ? data.ref.trim() : "";
  if (!ref) throw new Error("Servidor não retornou a referência da NFC-e.");
  const danfe = typeof data.danfe_url === "string" ? data.danfe_url.trim() : undefined;
  const pdfUrl = typeof data.pdf_url === "string" ? data.pdf_url.trim() : undefined;
  return {
    success: true,
    authorized: data.authorized === true,
    pending: data.pending !== false,
    status: typeof data.status === "string" ? data.status.trim() : undefined,
    ref,
    ...(danfe ? { danfe_url: danfe } : {}),
    ...(pdfUrl ? { pdf_url: pdfUrl } : {}),
    ...(typeof data.chave === "string" && data.chave.trim() ? { chave: data.chave.trim() } : {}),
    ...(typeof data.numero === "string" && data.numero.trim() ? { numero: data.numero.trim() } : {}),
    ...(typeof data.serie === "string" && data.serie.trim() ? { serie: data.serie.trim() } : {}),
    ...(typeof data.protocolo === "string" && data.protocolo.trim() ? { protocolo: data.protocolo.trim() } : {}),
    ...(typeof data.data_emissao === "string" && data.data_emissao.trim() ? { data_emissao: data.data_emissao.trim() } : {}),
    ...(typeof data.qrcode_url === "string" && data.qrcode_url.trim() ? { qrcode_url: data.qrcode_url.trim() } : {}),
    ...(typeof data.url_consulta === "string" && data.url_consulta.trim() ? { url_consulta: data.url_consulta.trim() } : {}),
    ...(data.emitente && typeof data.emitente === "object" ? { emitente: data.emitente } : {}),
    ...(data.destinatario && typeof data.destinatario === "object" ? { destinatario: data.destinatario } : {}),
    ...(Array.isArray(data.itens) ? { itens: data.itens } : {}),
    ...(Array.isArray(data.pagamentos) ? { pagamentos: data.pagamentos } : {}),
    ...(typeof data.subtotal === "number" ? { subtotal: data.subtotal } : {}),
    ...(typeof data.desconto === "number" ? { desconto: data.desconto } : {}),
    ...(typeof data.total === "number" ? { total: data.total } : {}),
    ...(typeof data.valor_total_tributos === "number" ? { valor_total_tributos: data.valor_total_tributos } : {}),
  };
}

/** Consulta status da NFC-e e retorna DANFE quando disponível. */
export async function nfceStatus(ref: string): Promise<NfceEmitResponse> {
  const url = `${config.BASE_URL}/nfce/status.php?ref=${encodeURIComponent(ref)}`;
  const res = await fetch(url, { ...defaultOptions, method: "GET" });
  const text = await res.text().then((t) => t.trim());
  let data: {
    ok?: boolean;
    success?: boolean;
    authorized?: boolean;
    pending?: boolean;
    status?: string;
    ref?: string;
    error?: string;
    danfe_url?: string;
    pdf_url?: string;
    chave?: string;
    numero?: string;
    serie?: string;
    protocolo?: string;
    data_emissao?: string;
    qrcode_url?: string;
    url_consulta?: string;
    emitente?: { nome?: string; cnpj?: string; ie?: string; endereco?: string };
    destinatario?: { cpf?: string; cnpj?: string; nome?: string };
    itens?: Array<{ descricao?: string; quantidade?: number; valor_unitario?: number; total?: number }>;
    pagamentos?: Array<{ descricao?: string; valor?: number }>;
    subtotal?: number;
    desconto?: number;
    total?: number;
    valor_total_tributos?: number;
  };
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    throw new Error("Resposta inválida do servidor");
  }
  if (!res.ok || data.ok === false || data.success === false) {
    throw new Error(typeof data.error === "string" ? data.error : "Erro ao consultar status da NFC-e");
  }
  const nextRef = typeof data.ref === "string" && data.ref.trim() !== "" ? data.ref.trim() : ref;
  const danfe = typeof data.danfe_url === "string" ? data.danfe_url.trim() : undefined;
  const pdfUrl = typeof data.pdf_url === "string" ? data.pdf_url.trim() : undefined;
  return {
    success: true,
    authorized: data.authorized === true,
    pending: data.pending !== false,
    status: typeof data.status === "string" ? data.status.trim() : undefined,
    ref: nextRef,
    ...(danfe ? { danfe_url: danfe } : {}),
    ...(pdfUrl ? { pdf_url: pdfUrl } : {}),
    ...(typeof data.chave === "string" && data.chave.trim() ? { chave: data.chave.trim() } : {}),
    ...(typeof data.numero === "string" && data.numero.trim() ? { numero: data.numero.trim() } : {}),
    ...(typeof data.serie === "string" && data.serie.trim() ? { serie: data.serie.trim() } : {}),
    ...(typeof data.protocolo === "string" && data.protocolo.trim() ? { protocolo: data.protocolo.trim() } : {}),
    ...(typeof data.data_emissao === "string" && data.data_emissao.trim() ? { data_emissao: data.data_emissao.trim() } : {}),
    ...(typeof data.qrcode_url === "string" && data.qrcode_url.trim() ? { qrcode_url: data.qrcode_url.trim() } : {}),
    ...(typeof data.url_consulta === "string" && data.url_consulta.trim() ? { url_consulta: data.url_consulta.trim() } : {}),
    ...(data.emitente && typeof data.emitente === "object" ? { emitente: data.emitente } : {}),
    ...(data.destinatario && typeof data.destinatario === "object" ? { destinatario: data.destinatario } : {}),
    ...(Array.isArray(data.itens) ? { itens: data.itens } : {}),
    ...(Array.isArray(data.pagamentos) ? { pagamentos: data.pagamentos } : {}),
    ...(typeof data.subtotal === "number" ? { subtotal: data.subtotal } : {}),
    ...(typeof data.desconto === "number" ? { desconto: data.desconto } : {}),
    ...(typeof data.total === "number" ? { total: data.total } : {}),
    ...(typeof data.valor_total_tributos === "number" ? { valor_total_tributos: data.valor_total_tributos } : {}),
  };
}

export async function vendasCriar(payload: VendaCriarPayload): Promise<{ venda_id: number }> {
  const data = await postVendaCreate(payload);

  const vendaId = Number(data.venda_id);
  if (!Number.isFinite(vendaId) || vendaId <= 0) {
    throw new Error('Resposta inválida do servidor: venda não confirmada.');
  }

  return { venda_id: vendaId };
}

// --- Desperdícios (itens = insumos + produtos sem composição) ---

export interface InsumoCaixa {
  id: number;
  nome: string;
  unidade_medida: string;
}

/** GET /api/insumos/list-caixa.php ? { ok, insumos: [{ id, nome, unidade_medida }] } */
export async function fetchInsumosCaixa(): Promise<InsumoCaixa[]> {
  const url = `${config.BASE_URL}/insumos/list-caixa.php`;
  try {
    const res = await fetch(url, { ...defaultOptions, method: 'GET' });
    const text = await res.text().then((t) => t.trim());
    if (!text) return [];
    const data = text ? (JSON.parse(text) as { ok?: boolean; insumos?: unknown[] }) : {};
    const raw = data?.insumos;
    if (!Array.isArray(raw)) return [];
    return raw.map((r: unknown) => {
      const row = r as Record<string, unknown>;
      return {
        id: Number(row.id) || 0,
        nome: String(row.nome ?? ''),
        unidade_medida: String(row.unidade_medida ?? row.unidade ?? 'un'),
      };
    });
  } catch {
    return [];
  }
}

/** Item pesquisável no modal de desperdícios: insumo ou produto (com ou sem composição) */
export interface ItemDesperdicio {
  tipo: 'insumo' | 'produto';
  id: number;
  nome: string;
  unidade_medida: string;
}

/** Monta lista para o modal: insumos + todos os produtos (sem e com composição) */
export function buildItensDesperdicio(insumos: InsumoCaixa[], produtos: ProdutoCaixa[]): ItemDesperdicio[] {
  const insumosList: ItemDesperdicio[] = insumos.map((i) => ({
    tipo: 'insumo',
    id: i.id,
    nome: i.nome,
    unidade_medida: i.unidade_medida,
  }));
  const produtosList: ItemDesperdicio[] = produtos.map((p) => ({
    tipo: 'produto' as const,
    id: p.id,
    nome: p.nome,
    unidade_medida: 'un',
  }));
  return [...insumosList, ...produtosList].sort((a, b) => a.nome.localeCompare(b.nome));
}

export type MotivoDesperdicio = 'consumo_interno' | 'vencimento' | 'brinde_propaganda' | 'cortesia_avaliacao' | 'avaria';

/** POST /api/desperdicios/create-from-caixa.php - Desperdício a partir do carrinho (só produtos). */
export interface CreateDesperdicioFromCaixaPayload {
  responsavel_nome: string;
  motivo: MotivoDesperdicio;
  itens: Array<{
    produto_id: number;
    nome: string;
    quantidade: number;
    preco_unitario: number;
  }>;
  total: number;
}

export async function createDesperdicioFromCaixa(payload: CreateDesperdicioFromCaixaPayload): Promise<{ desperdicio_id: number }> {
  const url = `${config.BASE_URL}/desperdicios/create-from-caixa.php`;
  const body = {
    responsavel_nome: payload.responsavel_nome,
    motivo: payload.motivo,
    itens: payload.itens,
    total: payload.total,
  };
  let res: Response;
  try {
    res = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    throw new Error('Falha de rede/CORS: ' + msg);
  }
  const text = await res.text().then((t) => t.trim());
  let data: { ok?: boolean; error?: string; desperdicio_id?: number };
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    throw new Error(res.ok ? 'Resposta inválida do servidor' : `HTTP ${res.status}`);
  }
  const errorMsg = typeof data?.error === 'string' ? data.error : null;
  if (!res.ok) {
    throw new Error(errorMsg ?? `HTTP ${res.status}`);
  }
  if (data && (data as { ok?: boolean }).ok === false) {
    throw new Error(errorMsg ?? 'Erro ao registrar desperdício');
  }
  return { desperdicio_id: (data as { desperdicio_id?: number }).desperdicio_id ?? 0 };
}

// --- Relatório de caixa e movimentos (entrada/retirada) ---

/** @deprecated use RelatorioDiaCompleto */
export type RelatorioDia = RelatorioDiaCompleto;

export async function fetchRelatorioDia(date?: string): Promise<RelatorioDiaCompleto> {
  const url = date
    ? `${config.BASE_URL}/caixa/relatorio-dia.php?date=${encodeURIComponent(date)}`
    : `${config.BASE_URL}/caixa/relatorio-dia.php`;
  const res = await fetch(url, { ...defaultOptions, method: 'GET' });
  const text = await res.text().then((t) => t.trim());
  let data: { ok?: boolean; error?: string } & Record<string, unknown>;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    throw new Error('Resposta inválida do servidor');
  }
  if (!res.ok || (data && (data as { ok?: boolean }).ok === false)) {
    throw new Error(typeof data?.error === 'string' ? data.error : 'Erro ao carregar relatório');
  }
  return normalizarRelatorioDia(data);
}

/** Envia o relatório do dia a todos os admins com e-mail válido (servidor). Sem limite de envios por dia. */
export async function enviarRelatorioPorEmail(date?: string): Promise<{ message: string; emails: string[] }> {
  const url = `${config.BASE_URL}/caixa/relatorio-email.php`;
  const body: { date?: string } = {};
  if (date && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    body.date = date;
  }
  const res = await fetch(url, {
    ...defaultOptions,
    method: 'POST',
    body: JSON.stringify(body),
  });
  const text = await res.text().then((t) => t.trim());
  let data: {
    ok?: boolean;
    success?: boolean;
    error?: string;
    message?: string;
    email?: string;
    emails?: string[];
  };
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    throw new Error('Resposta inválida do servidor');
  }
  const apiSuccess = data.ok === true || data.success === true;
  if (!apiSuccess) {
    const err =
      typeof data.error === 'string' && data.error.trim() !== ''
        ? data.error
        : typeof data.message === 'string' && data.message.trim() !== ''
          ? data.message
          : !res.ok
            ? `Erro ao enviar relatório por e-mail (HTTP ${res.status})`
            : 'Erro ao enviar relatório por e-mail';
    throw new Error(err);
  }
  const emails = Array.isArray(data.emails)
    ? data.emails.filter((e): e is string => typeof e === 'string' && e.trim() !== '').map((e) => e.trim())
    : [];
  const fallback =
    typeof data.email === 'string' && data.email.trim() !== ''
      ? data.email.split(',').map((e) => e.trim()).filter(Boolean)
      : [];
  const allEmails = emails.length > 0 ? emails : fallback;
  if (allEmails.length === 0) {
    throw new Error('Resposta do servidor sem destinatários.');
  }
  const message =
    typeof data.message === 'string' && data.message.trim() !== ''
      ? data.message.trim()
      : 'Relatório enviado com sucesso';
  return { message, emails: allEmails };
}

export async function createMovimentoCaixa(payload: {
  tipo: 'entrada' | 'retirada';
  valor: number;
  motivo?: string;
  /** Retirada obrigatório: compras | despesa | outros. */
  tipo_retirada?: 'compras' | 'despesa' | 'outros';
  /** @deprecated usar tipo_retirada */
  classificacao?: 'compras' | 'despesa' | 'outros';
}): Promise<{ id: number; tipo: string; valor: number; motivo: string | null; createdAt: string }> {
  const url = `${config.BASE_URL}/caixa/movimento.php`;
  const res = await fetch(url, {
    ...defaultOptions,
    method: 'POST',
    body: JSON.stringify(payload),
  });
  const text = await res.text().then((t) => t.trim());
  let data: { ok?: boolean; error?: string; id?: number; tipo?: string; valor?: number; motivo?: string | null; createdAt?: string };
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    throw new Error('Resposta inválida do servidor');
  }
  if (!res.ok || (data && (data as { ok?: boolean }).ok === false)) {
    throw new Error(typeof data?.error === 'string' ? data.error : 'Erro ao registrar movimento');
  }
  return {
    id: data.id ?? 0,
    tipo: data.tipo ?? payload.tipo,
    valor: data.valor ?? payload.valor,
    motivo: data.motivo ?? null,
    createdAt: data.createdAt ?? '',
  };
}

/** POST /api/desperdicios/create.php - Desperdício por item (insumo ou produto solto). Usado pelo DesperdiciosModal legado. */
export async function createDesperdicio(payload: {
  tipo_item: 'insumo' | 'produto';
  item_id: number;
  quantidade: number;
  motivo: MotivoDesperdicio;
  observacao?: string | null;
}): Promise<void> {
  const url = `${config.BASE_URL}/desperdicios/create.php`;
  const res = await fetch(url, {
    ...defaultOptions,
    method: 'POST',
    body: JSON.stringify(payload),
  });
  const text = await res.text().then((t) => t.trim());
  let data: { ok?: boolean; error?: string };
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    throw new Error('Resposta inválida do servidor');
  }
  if (!res.ok) {
    throw new Error(typeof data?.error === 'string' ? data.error : 'Erro ao registrar desperdício');
  }
  if (data && (data as { ok?: boolean }).ok === false) {
    throw new Error(typeof (data as { error?: string }).error === 'string' ? (data as { error: string }).error : 'Erro ao registrar desperdício');
  }
}

/** GET /api/desperdicios/responsaveis.php - Lista de nomes para autocomplete. */
export async function fetchResponsaveisDesperdicio(): Promise<string[]> {
  const url = `${config.BASE_URL}/desperdicios/responsaveis.php`;
  try {
    const res = await fetch(url, { ...defaultOptions, method: 'GET' });
    const text = await res.text().then((t) => t.trim());
    if (!text) return [];
    const data = JSON.parse(text) as { ok?: boolean; responsaveis?: unknown[] };
    const raw = data?.responsaveis;
    return Array.isArray(raw) ? raw.map((x) => String(x)) : [];
  } catch {
    return [];
  }
}
